import { StripeProduct } from "@kvix/shared";
import { Button, Text } from "@kvix/ui";
import { Box } from "@material-ui/core";
import {
  differenceInDays,
  format,
  formatDistanceToNowStrict,
  isFuture,
} from "date-fns";
import { sv } from "date-fns/locale";
import i18next, { TFunction } from "i18next";
import React, { FC, Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAsyncFn } from "react-use";
import { useHistory } from "react-router";
import StripeInternal from "stripe";
import styled from "styled-components";
import { PurchaseFlowContext } from "../../../../../../contexts/PurchaseFlow";
import { KvixUserContext } from "../../../../../../contexts/user";
import { useIsNativeApp } from "../../../../../../hooks/nativeApp";
import { formatCurrency } from "../../../../../../utils/formatCurrency";
import { DefaultFlow, NewFlow } from "../../../../../PurchaseFlow";
import { PaymentContext } from "../../../../Payment/context";
import { CancelSubscriptionModal } from "../../../../Payment/Subscription/Cancel";
import { EditSubscriptionModal } from "../../../../Payment/Subscription/Edit";
import { ProfileSection } from "../../../partials/Section";
import { ProfileSectionRow } from "../../../partials/SectionRow";
import Cookies from "js-cookie";

interface SubscriptionDetailsProps {
  subscription: StripeInternal.Subscription;
}

const getPlanMapping = (t: TFunction) => {
  const planMapping: Record<StripeInternal.Plan.Interval, string> = {
    day: t("account.planDetails.planMapping.day"),
    week: t("account.planDetails.planMapping.week"),
    month: t("account.planDetails.planMapping.month"),
    year: t("account.planDetails.planMapping.year"),
  };
  return planMapping;
};

// TODO Change from plan to price(plan will probably be deprecated in the furture)
const getProductName = (plan: StripeInternal.Plan) => {
  const product = plan.product;

  if (typeof product === "string") {
    throw new Error("Expected product to be an object!");
  }

  if (typeof product.deleted === "boolean") {
    return "-";
  }

  return (product as StripeInternal.Product).name;
};

const getCouponAmount = (coupon: StripeInternal.Coupon) => {
  if (coupon.percent_off) {
    return `${coupon.percent_off}%`;
  }

  if (coupon.amount_off) {
    return formatCurrency(coupon.amount_off / 100, coupon.currency);
  }
};

const getCouponDescription = (coupon: StripeInternal.Coupon, t: TFunction) => {
  const amount = getCouponAmount(coupon);

  if (
    coupon.duration === "once" ||
    (coupon.duration === "repeating" && coupon.duration_in_months === 1)
  ) {
    return t("account.planDetails.couponDescriptionsShort.once", {
      amount: amount,
    });
  }

  if (coupon.duration === "repeating") {
    return t("account.planDetails.couponDescriptionsShort.repeating", {
      amount: amount,
      months: coupon.duration_in_months,
    });
  }

  if (coupon.duration === "forever") {
    return t("account.planDetails.couponDescriptionsShort.forever", {
      amount: amount,
    });
  }
};

const CancelledSubscriptionDetails: FC<SubscriptionDetailsProps> = ({
  subscription,
}) => {
  const now = new Date();

  const cancelDate = new Date(subscription.cancel_at * 1000);
  const days = differenceInDays(cancelDate, now);

  const billingDate = new Date(subscription.current_period_end * 1000);
  const billing = format(billingDate, "dd/MM/yyy");
  const { t, ready } = useTranslation(["account", "general"]);

  if (!ready) {
    return null;
  }

  return (
    <Box maxWidth={500}>
      <Text variant="body2">
        {t("account.planDetails.cancelledText", { days, billing })}
      </Text>
    </Box>
  );
};

const ClickableText = styled(Text)`
  user-select: none;

  &:hover {
    cursor: pointer;
    filter: brightness(0.8);
    text-decoration: underline;
  }
`;

const SubscriptionDetails: FC<SubscriptionDetailsProps> = ({
  subscription,
}) => {
  const { t, ready } = useTranslation(["account", "general"]);
  const { actions } = useContext(PaymentContext);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const prettyInterval =
    getPlanMapping(t)[subscription.items.data[0].price.recurring.interval];

  const [{ loading }, resumeSubscription] = useAsyncFn(async () => {
    await actions.resumeSubscription(subscription);
  });

  const currentLanguage = i18next.language;
  const isCancelled = subscription.cancel_at_period_end;

  const nextBillingDate = new Date(subscription.current_period_end * 1000);
  const subscribedDate = new Date(subscription.created * 1000);
  const trialEndDate = subscription.trial_end
    ? new Date(subscription.trial_end * 1000)
    : null;

  // TODO Remove plan(plan will probably be deprecated in the furture)
  const product = getProductName(
    (
      subscription as StripeInternal.Subscription & {
        plan: StripeInternal.Plan;
      }
    ).plan
  );
  const coupon = subscription.discount?.coupon;

  if (!ready) {
    return null;
  }

  return (
    <>
      <ProfileSectionRow
        action={
          isCancelled ? (
            <Button
              size="small"
              variant="text"
              onClick={resumeSubscription}
              loading={loading}
            >
              {t("general:buttons.resume")}
            </Button>
          ) : (
            <Button
              size="small"
              variant="text"
              onClick={() => setCancelModalOpen(true)}
            >
              {t("general:buttons.cancel")}
            </Button>
          )
        }
      >
        <Text gutterBottom weight={600}>
          {product}
        </Text>
      </ProfileSectionRow>

      {isCancelled && (
        <ProfileSectionRow>
          <CancelledSubscriptionDetails subscription={subscription} />
        </ProfileSectionRow>
      )}

      {!isCancelled && (
        <Fragment>
          <ProfileSectionRow
            action={
              <ClickableText
                color="primary"
                onClick={() => setEditModalOpen(true)}
              >
                {prettyInterval}
              </ClickableText>
            }
          >
            <Text>{t("account.planDetails.billingFrequency")}</Text>
          </ProfileSectionRow>

          <ProfileSectionRow
            action={
              <Text>
                {format(nextBillingDate, "dd/MM/yyyy", {
                  locale: currentLanguage === "sv-SE" && sv,
                })}
              </Text>
            }
          >
            <Text>{t("account.planDetails.nextBillingDate")}</Text>
          </ProfileSectionRow>

          <ProfileSectionRow
            action={
              <Text>
                {format(subscribedDate, "dd/MM/yyyy", {
                  locale: currentLanguage === "sv-SE" && sv,
                })}
              </Text>
            }
          >
            <Text>{t("account.planDetails.subscribedSince")}</Text>
          </ProfileSectionRow>

          {trialEndDate && isFuture(trialEndDate) && (
            <ProfileSectionRow
              action={
                <Text>
                  {t("account.planDetails.endsIn", {
                    end: formatDistanceToNowStrict(trialEndDate, {
                      locale: currentLanguage === "sv-SE" && sv,
                    }),
                  })}
                </Text>
              }
            >
              <Text>{t("account.planDetails.trialStatus")}</Text>
            </ProfileSectionRow>
          )}

          {coupon && (
            <ProfileSectionRow
              action={<Text>{getCouponDescription(coupon, t)}</Text>}
            >
              <Text>{t("account.planDetails.activeCoupon")}</Text>
            </ProfileSectionRow>
          )}
        </Fragment>
      )}

      <CancelSubscriptionModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        subscription={subscription}
      />

      <EditSubscriptionModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        subscription={subscription}
      />
    </>
  );
};

export const AccountSectionSubscriptions: FC = (props) => {
  const { customer } = useContext(PaymentContext);
  const { t, ready } = useTranslation(["account", "signUpCampaign"]);
  const isNativeApp = useIsNativeApp();
  const purchaseFlowContext = useContext(PurchaseFlowContext);
  const { user } = useContext(KvixUserContext);
  const [showNoSubscriptions, setShowNoSubscriptions] = useState(false);
  const [showStripeInformation, setShowStripeInformation] = useState(false);
  const history = useHistory();

  useEffect(() => {
    setShowNoSubscriptions(!isPayingCustomer());
  }, [user]);

  useEffect(() => {
    setShowStripeInformation(isStripeCustomer());
  }, [customer]);

  if (!ready) {
    return null;
  }

  const isStripeCustomer = () => {
    return customer.subscriptions && customer.subscriptions?.data.length > 0;
  };

  const is3rdPartyCustomer = () => {
    return (
      user.stripeProducts.includes(StripeProduct.LIVE) && !isStripeCustomer()
    );
  };

  const isPayingCustomer = () => {
    return (
      user.stripeProducts.length > 0 &&
      user.stripeProducts.includes(StripeProduct.LIVE)
    );
  };

  return (
    <ProfileSection
      title={
        <Text variant="h5" style={{ marginRight: 16 }}>
          {t("account.planDetails.title")}
        </Text>
      }
      action={
        <>
          {!isNativeApp && (
            <Link to="/redeem">
              <Button variant="text" size="small" lowercase>
                {t("account.paymentMethod.addGiftcard")}
              </Button>
            </Link>
          )}
        </>
      }
    >
      {showNoSubscriptions ? (
        <>
          <Text style={{ opacity: 0.5 }}>
            {t("account.planDetails.noSubscription")}
          </Text>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              const wpCampaignParams = Cookies.get('wp-campaign');
              
              if (wpCampaignParams) {
                history.replace(`${history.location.pathname}${wpCampaignParams}`);
              }
    
              purchaseFlowContext.actions.prompt(<NewFlow registerProps={{ checkoutSource: wpCampaignParams ? 'wp' : 'kvix' }} currentTab={1} />);
            }}
          >
            {t("signUpCampaign:offer.title")}
          </Button>
        </>
      ) : !isNativeApp ? (
        <>
          {showStripeInformation ? (
            <>
              {customer.subscriptions.data.map((subscription) => (
                <SubscriptionDetails
                  subscription={subscription}
                  key={subscription.id}
                />
              ))}
            </>
          ) : (
            <>
              <Text>
                {`${t("account.planDetails.subscriptionActiveFrom")}`}
              </Text>
            </>
          )}
        </>
      ) : (
        <Text color="primary">{t("account.planDetails.cancelInApp")}</Text>
      )}
    </ProfileSection>
  );
};
